/**
 * Cross-device progress without accounts. A sync code is a random,
 * unguessable id that OWNS a blob of {save, settings}: no email, no
 * password, no PII, and the same shape an account would later own (an
 * account, if it ever exists, is just a thing that holds codes).
 *
 * The merge is union-and-best, never overwrite: syncing two machines
 * cannot lose a record on either one, which is the property that makes a
 * manual sync safe to press at any moment.
 */

const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // no I/L/O/0/1
const CODE_LEN = 12;
const GROUP = 4;

/** A fresh sync code: 12 chars of ~59 bits, grouped for reading aloud. */
export function makeSyncCode() {
  const bytes = new Uint8Array(CODE_LEN);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < CODE_LEN; i++) out += CODE_ALPHABET[bytes[i] % CODE_ALPHABET.length];
  return out;
}

/** Display form: XXXX-XXXX-XXXX. The wire form has no dashes. */
export function formatSyncCode(code) {
  return (code ?? '').match(new RegExp(`.{1,${GROUP}}`, 'g'))?.join('-') ?? '';
}

/** Accepts pasted dashes, spaces and lower case; validates the alphabet. */
export function normalizeSyncCode(input) {
  const clean = String(input ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length !== CODE_LEN) return null;
  for (const ch of clean) if (!CODE_ALPHABET.includes(ch)) return null;
  return clean;
}

/**
 * Union-and-best merge of two save blobs. Lower times and higher depths
 * win, milestones take the max, cleared is a union, and a ghost follows
 * its record so the hologram always belongs to the winning run.
 */
export function mergeSaves(local, remote) {
  const a = local ?? {};
  const b = remote ?? {};
  const out = {
    cleared: { ...(b.cleared ?? {}), ...(a.cleared ?? {}) },
    best: { ...(b.best ?? {}) },
    tower: { ...(b.tower ?? {}) },
    ghosts: { ...(b.ghosts ?? {}) },
    topSpeed: Math.max(a.topSpeed ?? 0, b.topSpeed ?? 0),
    maxDrop: Math.max(a.maxDrop ?? 0, b.maxDrop ?? 0),
    runs: Math.max(a.runs ?? 0, b.runs ?? 0),
    tut: (a.tut || b.tut) ? 1 : 0,
    askedName: (a.askedName || b.askedName) ? 1 : 0,
  };
  for (const [id, t] of Object.entries(a.best ?? {})) {
    const other = out.best[id];
    if (other == null || t < other) {
      out.best[id] = t;
      // The ghost is evidence for the time; they travel together or the
      // hologram races a line that never set the record beside it.
      const g = a.ghosts?.[id];
      if (g) out.ghosts[id] = g;
      else delete out.ghosts[id];
    }
  }
  for (const [id, depth] of Object.entries(a.tower ?? {})) {
    const other = out.tower[id];
    if (other == null || depth > other) {
      out.tower[id] = depth;
      const g = a.ghosts?.[id];
      if (g) out.ghosts[id] = g;
      else delete out.ghosts[id];
    } else if (depth === other && out.ghosts[id] == null && a.ghosts?.[id]) {
      // Equal depths keep the cloud replay when present. A local replay
      // of the same depth can fill a missing one without borrowing a
      // shallower run; saves do not store a separate depth tie-break time.
      out.ghosts[id] = a.ghosts[id];
    }
  }
  // A sector with a ghost but no record on either side keeps whichever
  // ghost exists (harmless, and it is someone's only replay of the run).
  for (const [id, g] of Object.entries(a.ghosts ?? {})) {
    if (out.ghosts[id] == null && out.best[id] == null && out.tower[id] == null) out.ghosts[id] = g;
  }
  return out;
}

/**
 * Settings merge: the LOCAL device wins every preference it has set,
 * because preferences are about the machine in your hands (sensitivity,
 * quality, binds). Only the identity travels, and only to fill a blank.
 */
export function mergeSettings(local, remote) {
  const out = { ...(local ?? {}) };
  if (!out.handle && remote?.handle) out.handle = remote.handle;
  return out;
}

/** The Worker refuses a sync blob past 512KB; pack to a budget under it
 *  so a save full of long replays still uploads (audit finding 4: twelve
 *  four-minute ghosts serialised to 959KB and every push failed). */
export const SYNC_BUDGET_BYTES = 480 * 1024;

/**
 * Everything the blob carries. Ghosts are the bulk, so they are bounded
 * twice: by count, and by serialised size against the Worker's limit.
 * Records always travel; a replay that does not fit is left behind in the
 * order the save recorded them (earliest first), which is the explicit
 * preference: older records are usually the ones a second device lacks.
 */
export function packPayload(save, settings, maxGhosts = 20, maxBytes = SYNC_BUDGET_BYTES) {
  const ghosts = {};
  // Keep replays backed by either a timed record or a Gauntlet depth,
  // sharing the same count cap across both kinds of record.
  const ids = Object.keys(save.ghosts ?? {})
    .filter((id) => save.best?.[id] != null || save.tower?.[id] != null)
    .slice(0, maxGhosts);
  const base = {
    v: 1,
    save: { ...save, ghosts },
    settings: { handle: settings.handle ?? '' },
    at: Date.now(),
  };
  let bytes = JSON.stringify(base).length;
  for (const id of ids) {
    // The entry's cost on the wire: the key, the quotes, the colon, the comma.
    const cost = JSON.stringify(save.ghosts[id]).length + id.length + 4;
    if (bytes + cost > maxBytes) continue;
    ghosts[id] = save.ghosts[id];
    bytes += cost;
  }
  return base;
}
